import { useMemo, useState } from "react";
import type { DashboardCell } from "../lib/api";
import { fmtDateShort, fmtPrice, fmtUpdateTimeFull } from "../lib/format";
import { ChangeBadge } from "./ChangeBadge";

interface Props {
  rows: DashboardCell[];
}

type ColKey = "symbol" | "contract" | "bid" | "ask" | "last" | "chg" | "prev";

// Contract-type ordering: the user wants live-price rows grouped by
// tenor type first — all month contracts, then all quarters, then
// seasons, then calendar-year contracts — and only sorted by delivery
// date *within* each group. Plain delivery-date order interleaves them
// (M1 Jul-2026, Q1 Q3-2026, M2 Aug-2026, … Y1 Cal-2027, Q4 Q2-2027),
// which is what we want to avoid. Spot/ISO rows rank first; anything we
// can't classify ranks last so it falls to the bottom of its card.
const TYPE_RANK = {
  spot: 0,
  month: 1,
  quarter: 2,
  season: 3,
  year: 4,
  unknown: 5,
} as const;

type ContractSortKey = { typeRank: number; ms: number };

const UNKNOWN_KEY: ContractSortKey = {
  typeRank: TYPE_RANK.unknown,
  ms: Number.POSITIVE_INFINITY,
};

/**
 * Parse a Montel contractName into a sort key: a tenor-type rank plus the
 * delivery-start epoch (ms) within that type. Rows are grouped by type
 * (month → quarter → season → year) and ordered chronologically inside
 * each group. The rolling Qn codes don't tell us the delivery date — only
 * the resolved label does.
 *
 * Supported shapes:
 *   "Jul-2026", "Aug-2026", …   month
 *   "Q3-2026", "Q4-2026", …     quarter
 *   "Win-2026", "Sum-2026"      season (Oct/Apr starts respectively)
 *   "Cal-2027", …               calendar year
 *   "MidDec-2026", …            EUA Dec annual contract (year-tenor)
 *   "2026-06-07", …             ISO date (Spot rows)
 *
 * Returns the unknown rank with +Infinity ms when the value can't be
 * parsed so unrecognised contractName rows fall to the bottom — combined
 * with a stable sort that keeps them in their original positions relative
 * to each other.
 */
function contractSortKey(contractName: string | null | undefined): ContractSortKey {
  if (!contractName) return UNKNOWN_KEY;
  const name = contractName.trim();
  // Plain ISO date — used by Spot rows.
  if (/^\d{4}-\d{2}-\d{2}/.test(name)) {
    const t = Date.parse(name);
    return { typeRank: TYPE_RANK.spot, ms: Number.isFinite(t) ? t : Number.POSITIVE_INFINITY };
  }
  // "Cal-YYYY"
  let m = /^cal-?(\d{4})$/i.exec(name);
  if (m) return { typeRank: TYPE_RANK.year, ms: Date.UTC(Number(m[1]), 0, 1) };
  // "Qn-YYYY"
  m = /^q(\d)-?(\d{4})$/i.exec(name);
  if (m) return { typeRank: TYPE_RANK.quarter, ms: Date.UTC(Number(m[2]), (Number(m[1]) - 1) * 3, 1) };
  // Season "Sum-YYYY" / "Win-YYYY"
  m = /^(sum|win)-?(\d{4})$/i.exec(name);
  if (m) {
    return {
      typeRank: TYPE_RANK.season,
      ms: Date.UTC(Number(m[2]), m[1].toLowerCase() === "sum" ? 3 : 9, 1),
    };
  }
  // EUA "MidDec-YYYY" — an annual contract, so it sorts with the years.
  m = /^middec-?(\d{4})$/i.exec(name);
  if (m) return { typeRank: TYPE_RANK.year, ms: Date.UTC(Number(m[1]), 11, 15) };
  // Month codes like "Jul-2026" — accept English + a few German variants
  // ("Mai", "Mär", "Okt", "Dez").
  const months: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, mär: 2, apr: 3, may: 4, mai: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, okt: 9, nov: 10, dec: 11, dez: 11,
  };
  m = /^([A-Za-zäöüÄÖÜ]{3,4})-?(\d{4})$/.exec(name);
  if (m) {
    const monthIdx = months[m[1].toLowerCase()];
    if (monthIdx !== undefined) {
      return { typeRank: TYPE_RANK.month, ms: Date.UTC(Number(m[2]), monthIdx, 1) };
    }
  }
  return UNKNOWN_KEY;
}

/**
 * Price table for one product group. Two visual affordances on top of
 * the bare data:
 *
 * 1. **ASK column subtly highlighted.** ASK is the most actionable
 *    number for someone reading the dashboard, so every cell in that
 *    column gets a very light tinted background to set up a clear
 *    visual hierarchy. Pure cosmetic, no behaviour change.
 *
 * 2. **Cross-highlight on hover.** When the cursor enters a cell, both
 *    the row and the column it sits in get a slight background tint so
 *    the user can find their place in dense Q1-Q8/Y1-Y4 futures grids
 *    without losing track of which row/column belongs together. State
 *    is local to the component so hovering one card doesn't bleed
 *    into the others.
 */
export function PriceTable({ rows }: Props) {
  const [hovered, setHovered] = useState<{ row: number; col: ColKey } | null>(null);

  // Header of the "Prev" column shows the date these previous-settlement values
  // belong to (delivery day for spot, trading day for futures). It's uniform
  // per table, so any row's priceDate works; fall back to "Prev" if unknown.
  const headerDate = rows.find((r) => r.priceDate)?.priceDate ?? null;

  // Group rows by tenor type (months first, then quarters, then seasons,
  // then years) and sort chronologically within each group. Montel's
  // rolling Q1 isn't necessarily before Q3 calendar-wise; the resolved
  // contractName ("Q3-2026" / "Q1-2027" / …) is what the user wants to
  // see in order. Rows whose contractName we can't parse keep their
  // original relative position thanks to Array.prototype.sort's
  // stability (ES2019+).
  const sortedRows = useMemo(() => {
    const indexed = rows.map((row, originalIndex) => ({
      row,
      originalIndex,
      sortKey: contractSortKey(row.contractName),
    }));
    indexed.sort((a, b) => {
      if (a.sortKey.typeRank !== b.sortKey.typeRank) {
        return a.sortKey.typeRank - b.sortKey.typeRank;
      }
      if (a.sortKey.ms !== b.sortKey.ms) return a.sortKey.ms - b.sortKey.ms;
      return a.originalIndex - b.originalIndex;
    });
    return indexed.map((r) => r.row);
  }, [rows]);

  const colCls = (col: ColKey, rowIdx: number) => {
    const askCol = col === "ask" ? "bg-sky-50/60 dark:bg-sky-500/[0.07]" : "";
    const rowHover = hovered?.row === rowIdx ? "bg-slate-100/60 dark:bg-slate-800/40" : "";
    const colHover = hovered?.col === col ? "bg-slate-100/60 dark:bg-slate-800/40" : "";
    return [askCol, rowHover, colHover].filter(Boolean).join(" ");
  };

  const headerCls = (col: ColKey) => {
    const ask = col === "ask" ? "bg-sky-50/80 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300" : "";
    const colHover = hovered?.col === col ? "bg-slate-100/60 dark:bg-slate-800/40" : "";
    return [ask, colHover].filter(Boolean).join(" ");
  };

  return (
    <table
      className="w-full text-[11px] sm:text-xs"
      onMouseLeave={() => setHovered(null)}
    >
      <thead>
        <tr className="text-left text-[9px] sm:text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
          <th className={`py-1.5 pr-1 sm:pr-1.5 font-medium ${headerCls("symbol")}`}>Symbol</th>
          <th className={`py-1.5 pr-1 sm:pr-1.5 font-medium ${headerCls("contract")}`}>Kontrakt</th>
          <th
            className={`py-1.5 pr-1 sm:pr-1.5 text-right font-medium hidden sm:table-cell ${headerCls("bid")}`}
          >
            Bid
          </th>
          <th
            className={`py-1.5 pr-1 sm:pr-1.5 text-right font-medium ${headerCls("ask")}`}
          >
            Ask
          </th>
          <th className={`py-1.5 pr-1 sm:pr-1.5 text-right font-medium ${headerCls("last")}`}>Last</th>
          <th className={`py-1.5 pr-1 sm:pr-1.5 text-right font-medium ${headerCls("chg")}`}>Chg</th>
          <th
            className={`py-1.5 pr-0 text-right font-medium ${headerCls("prev")}`}
            title="Previous Settlement"
          >
            {headerDate ? fmtDateShort(headerDate) : "Prev"}
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row, i) => {
          const updateTitle = fmtUpdateTimeFull(row.updateTime);
          const title = row.error
            ? `${row.error}${updateTitle ? `\n${updateTitle}` : ""}`
            : updateTitle || undefined;
          const onEnter = (col: ColKey) => () => setHovered({ row: i, col });
          return (
            <tr
              key={`${row.label}-${i}`}
              className={`border-b border-slate-100 dark:border-slate-800/60 last:border-0 ${
                row.error ? "opacity-60" : ""
              }`}
              title={title}
            >
              <td
                className={`py-1 pr-1 sm:pr-1.5 font-medium truncate max-w-[11ch] ${colCls("symbol", i)}`}
                onMouseEnter={onEnter("symbol")}
              >
                <span title={row.label}>{row.label}</span>
              </td>
              <td
                className={`py-1 pr-1 sm:pr-1.5 text-slate-600 dark:text-slate-400 truncate max-w-[10ch] ${colCls("contract", i)}`}
                onMouseEnter={onEnter("contract")}
              >
                <span title={row.contractName ?? undefined}>{row.contractName ?? "—"}</span>
              </td>
              <td
                className={`py-1 pr-1 sm:pr-1.5 text-right tabular-nums whitespace-nowrap hidden sm:table-cell ${colCls("bid", i)}`}
                onMouseEnter={onEnter("bid")}
              >
                {fmtPrice(row.bid)}
              </td>
              <td
                className={`py-1 pr-1 sm:pr-1.5 text-right tabular-nums whitespace-nowrap font-medium ${colCls("ask", i)}`}
                onMouseEnter={onEnter("ask")}
              >
                {fmtPrice(row.ask)}
              </td>
              <td
                className={`py-1 pr-1 sm:pr-1.5 text-right tabular-nums whitespace-nowrap font-medium ${colCls("last", i)}`}
                onMouseEnter={onEnter("last")}
              >
                {fmtPrice(row.last)}
              </td>
              <td
                className={`py-1 pr-1 sm:pr-1.5 text-right tabular-nums whitespace-nowrap ${colCls("chg", i)}`}
                onMouseEnter={onEnter("chg")}
              >
                <ChangeBadge value={row.change} />
              </td>
              <td
                className={`py-1 pr-0 text-right tabular-nums whitespace-nowrap text-slate-500 dark:text-slate-400 ${colCls("prev", i)}`}
                onMouseEnter={onEnter("prev")}
              >
                {fmtPrice(row.prevClose)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
